import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';

/**
 * Base button props extending motion button attributes (excluding conflicting props)
 */
interface BaseButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
    children: React.ReactNode;
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
}

/**
 * Primary button with Dexco green styling
 */
export const PrimaryButton: React.FC<BaseButtonProps> = ({
    children,
    isLoading = false,
    leftIcon,
    rightIcon,
    disabled,
    className = '',
    ...props
}) => {
    return (
        <motion.button
            whileHover={{ scale: disabled || isLoading ? 1 : 1.02 }}
            whileTap={{ scale: disabled || isLoading ? 1 : 0.98 }}
            className={`
        inline-flex items-center justify-center gap-2 px-6 py-3 
        font-body font-semibold text-sm rounded-lg flex-shrink-0
        bg-dexco-green-primary text-dexco-white
        hover:bg-dexco-green-hover
        disabled:bg-gray-400 disabled:cursor-not-allowed
        transition-colors duration-200
        focus:outline-none focus:ring-2 focus:ring-dexco-green-primary focus:ring-offset-2
        ${className}
      `}
            disabled={disabled || isLoading}
            {...props}
        >
            {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
                leftIcon
            )}
            {children}
            {!isLoading && rightIcon}
        </motion.button>
    );
};

/**
 * Secondary button with white background and green border
 */
export const SecondaryButton: React.FC<BaseButtonProps> = ({
    children,
    isLoading = false,
    leftIcon,
    rightIcon,
    disabled,
    className = '',
    ...props
}) => {
    return (
        <motion.button
            whileHover={{ scale: disabled || isLoading ? 1 : 1.02 }}
            whileTap={{ scale: disabled || isLoading ? 1 : 0.98 }}
            className={`
        inline-flex items-center justify-center gap-2 px-6 py-3 
        font-body font-semibold text-sm rounded-lg flex-shrink-0
        bg-dexco-white text-dexco-green-primary border-2 border-dexco-green-primary
        hover:bg-dexco-gray-bg
        disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed
        transition-colors duration-200
        focus:outline-none focus:ring-2 focus:ring-dexco-green-primary focus:ring-offset-2
        ${className}
      `}
            disabled={disabled || isLoading}
            {...props}
        >
            {isLoading ? (
                <div className="w-4 h-4 border-2 border-dexco-green-primary border-t-transparent rounded-full animate-spin" />
            ) : (
                leftIcon
            )}
            {children}
            {!isLoading && rightIcon}
        </motion.button>
    );
};

/**
 * Ghost button with transparent background
 */
export const GhostButton: React.FC<BaseButtonProps> = ({
    children,
    isLoading = false,
    leftIcon,
    rightIcon,
    disabled,
    className = '',
    ...props
}) => {
    return (
        <motion.button
            whileHover={{ scale: disabled || isLoading ? 1 : 1.02 }}
            whileTap={{ scale: disabled || isLoading ? 1 : 0.98 }}
            className={`
        inline-flex items-center justify-center gap-2 px-4 py-2 
        font-body font-semibold text-sm rounded-lg
        bg-transparent text-dexco-text-dark
        hover:bg-dexco-gray-bg
        disabled:text-gray-400 disabled:cursor-not-allowed
        transition-colors duration-200
        focus:outline-none focus:ring-2 focus:ring-dexco-green-primary focus:ring-offset-2
        ${className}
      `}
            disabled={disabled || isLoading}
            {...props}
        >
            {isLoading ? (
                <div className="w-4 h-4 border-2 border-dexco-text-dark border-t-transparent rounded-full animate-spin" />
            ) : (
                leftIcon
            )}
            {children}
            {!isLoading && rightIcon}
        </motion.button>
    );
};

/**
 * Icon button for actions like add, edit, delete
 */
interface IconButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
    icon: React.ReactNode;
    size?: 'sm' | 'md' | 'lg';
    variant?: 'primary' | 'secondary' | 'ghost';
    isLoading?: boolean;
    'aria-label': string;
}

export const IconButton: React.FC<IconButtonProps> = ({
    icon,
    size = 'md',
    variant = 'ghost',
    isLoading = false,
    disabled,
    className = '',
    ...props
}) => {
    const sizeClasses = {
        sm: 'w-8 h-8 p-1',
        md: 'w-10 h-10 p-2',
        lg: 'w-12 h-12 p-3',
    };

    const variantClasses = {
        primary: 'bg-dexco-green-primary text-dexco-white hover:bg-dexco-green-hover',
        secondary: 'bg-dexco-white text-dexco-green-primary border-2 border-dexco-green-primary hover:bg-dexco-gray-bg',
        ghost: 'bg-transparent text-dexco-text-dark hover:bg-dexco-gray-bg',
    };

    return (
        <motion.button
            whileHover={{ scale: disabled || isLoading ? 1 : 1.1 }}
            whileTap={{ scale: disabled || isLoading ? 1 : 0.95 }}
            className={`
        inline-flex items-center justify-center rounded-lg
        ${sizeClasses[size]}
        ${variantClasses[variant]}
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors duration-200
        focus:outline-none focus:ring-2 focus:ring-dexco-green-primary focus:ring-offset-2
        ${className}
      `}
            disabled={disabled || isLoading}
            {...props}
        >
            {isLoading ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
                icon
            )}
        </motion.button>
    );
}; 